/**
 * =============================================================================
 * Página /tenant/content/templates — Modelos do Content Studio
 *
 * Lista os modelos da conta (proposta, orçamento, página pública, e-mail,
 * relatório) com busca e filtros por tipo e situação, usando os helpers padrão
 * de listagem do sistema. A criação abre um rascunho vazio e leva direto ao
 * construtor visual.
 *
 * Todo acesso a dados passa por RPCs (`src/lib/studio/api.ts`).
 *
 * @version 1.0.0
 * RELEASE: 2026-09-09 v1.0.0 - Versão inicial (Studio, Fase 4).
 * =============================================================================
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, Plus, Search } from 'lucide-react';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageFiltersBar,
  ListPageContent,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { useSaveStudioDraft, useStudioTemplates } from '@/hooks/useStudioTemplates';
import {
  createEmptyDocument,
  listCreatableTemplateDefinitions,
  listTemplateDefinitions,
  type StudioTemplateType,
} from '@/lib/studio/core';
import { DEFAULT_LANDING_DOCUMENT } from '@/lib/studio/templates/defaultLandingTemplate';
import { DEFAULT_ONLINE_PROPOSAL_DOCUMENT } from '@/lib/studio/templates/defaultOnlineProposalTemplate';

import { toast } from '@/lib/toast';
import { logger } from '@/lib/logger';

/** Cores da bolinha de situação (padrão visual das demais listagens). */
const COR_SITUACAO: Record<string, string> = {
  draft: 'bg-amber-500',
  published: 'bg-emerald-500',
  archived: 'bg-muted-foreground',
};

const ROTULO_SITUACAO: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  archived: 'Arquivado',
};

export default function StudioTemplates() {
  const navigate = useNavigate();
  const salvar = useSaveStudioDraft();

  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState<string>('todos');
  const [situacao, setSituacao] = useState<string>('todas');

  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [tipoNovo, setTipoNovo] = useState<StudioTemplateType>('proposal');

  // Filtro da listagem mostra todos os tipos; a criação mostra só os liberados
  const tipos = useMemo(() => listTemplateDefinitions(), []);
  const tiposCriaveis = useMemo(() => listCreatableTemplateDefinitions(), []);


  const filtros = useMemo(
    () => ({
      search: busca.trim() || null,
      type: tipo === 'todos' ? null : (tipo as StudioTemplateType),
      status: situacao === 'todas' ? null : (situacao as 'draft' | 'published' | 'archived'),
      limit: 50,
      offset: 0,
    }),
    [busca, tipo, situacao],
  );

  const { data, isLoading, isFetching } = useStudioTemplates(filtros);
  const itens = data?.items ?? [];

  /**
   * Cria o modelo e abre o construtor.
   * Landing pages nascem com estrutura inicial (capa, destaques, depoimentos e
   * formulário); a Proposta Online nasce com a estrutura da proposta; demais
   * tipos começam vazios.
   */
  function documentoInicial(t: StudioTemplateType) {
    if (t === 'landing_page') return JSON.parse(JSON.stringify(DEFAULT_LANDING_DOCUMENT));
    if (t === 'online_proposal') return JSON.parse(JSON.stringify(DEFAULT_ONLINE_PROPOSAL_DOCUMENT));
    return createEmptyDocument();
  }

  async function aoCriar() {
    if (!nome.trim()) {
      toast.error('Informe o nome do modelo.');
      return;
    }
    try {
      const resultado = await salvar.mutateAsync({
        name: nome.trim().slice(0, 120),
        type: tipoNovo,
        document: documentoInicial(tipoNovo),
        outputModes: listTemplateDefinitions().find((t) => t.type === tipoNovo)?.outputs ?? ['web'],
      });
      setCriando(false);

      setNome('');
      navigate(`/tenant/content/templates/${resultado.template_id}`);
    } catch (erro) {
      logger.error('studio', 'Falha ao criar modelo', erro);
      toast.error('Não foi possível criar o modelo.');
    }
  }

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Modelos"
        actions={
          <Button size="sm" className="h-8 gap-1.5" onClick={() => setCriando(true)}>
            <Plus className="h-3.5 w-3.5" />
            Novo modelo
          </Button>
        }
      />

      <ListPageFiltersBar isFetching={isFetching}>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome"
            className={`${LIST_FILTERS_INPUT_CLASS} pl-7`}
            maxLength={120}
          />
        </div>

        <Select value={tipo} onValueChange={setTipo}>
          <SelectTrigger className={LIST_FILTERS_SELECT_TRIGGER_CLASS}>
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os tipos</SelectItem>
            {tipos.map((t) => (
              <SelectItem key={t.type} value={t.type}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={situacao} onValueChange={setSituacao}>
          <SelectTrigger className={LIST_FILTERS_SELECT_TRIGGER_CLASS}>
            <SelectValue placeholder="Situação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas as situações</SelectItem>
            <SelectItem value="draft">Rascunho</SelectItem>
            <SelectItem value="published">Publicado</SelectItem>
            <SelectItem value="archived">Arquivado</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent>
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modelo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="text-center">Versões</TableHead>
                <TableHead className="text-right">Atualizado em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && itens.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                    Nenhum modelo por aqui ainda. Crie o primeiro para começar.
                  </TableCell>
                </TableRow>
              )}

              {itens.map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/tenant/content/templates/${item.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{item.name}</p>
                        {item.description && (
                          <p className="truncate text-xs text-muted-foreground">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {tipos.find((t) => t.type === item.type)?.label ?? item.type}
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2 text-sm">
                      <span
                        className={`h-2 w-2 rounded-full ${COR_SITUACAO[item.status] ?? 'bg-muted'}`}
                      />
                      {ROTULO_SITUACAO[item.status] ?? item.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="h-5 text-[10px]">
                      {item.versions_count}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {new Date(item.updated_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </ListPageContent>

      {/* ------------------------------------------------- Criação de modelo */}
      <Dialog open={criando} onOpenChange={setCriando}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Novo modelo</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="studio-nome">Nome</Label>
              <Input
                id="studio-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Proposta comercial padrão"
                maxLength={120}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="studio-tipo">Tipo</Label>
              <Select
                value={tipoNovo}
                onValueChange={(v) => setTipoNovo(v as StudioTemplateType)}
              >
                <SelectTrigger id="studio-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiposCriaveis.map((t) => (
                    <SelectItem key={t.type} value={t.type}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Explica a diferença entre Proposta Online e Landing Page */}
              <p className="text-xs text-muted-foreground">
                {tiposCriaveis.find((t) => t.type === tipoNovo)?.hint}
              </p>
            </div>

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCriando(false)}>
              Cancelar
            </Button>
            <Button onClick={aoCriar} disabled={salvar.isPending}>
              {salvar.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Criar e abrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListPageLayout>
  );
}
